/**
 * Preferred Creditors View — 2-tab layout: Preferred Creditors + Agent feed.
 *
 * The employee list reads directly from the `employeeEntries` table,
 * not from `summarizationTasks.output`.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  Users,
  Bot,
  MessageSquare,
  Plus,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { UnifiedFeed } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import type { EmploymentViewProps } from "./types";
import { EmployeeEntitlementsPanel } from "./EmployeeEntitlementsPanel";
import { buildPreferredCreditorsExport } from "./buildPreferredCreditorsExport";

export function EmploymentView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
}: EmploymentViewProps) {
  const [chatOpen, setChatOpen] = useState(false);
  // Lets the practitioner open the table before the agent has run so they can
  // record employees they already know about.
  const [manualEntry, setManualEntry] = useState(false);
  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  // Canonical register feeds the export. task.output is only counts and
  // narrative, which is why the old Field/Value dump was useless.
  const { data: entries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listEmployeeEntriesForJob,
      { jobId },
    ),
  );

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting" && !manualEntry;

  const output = task.output as
    | {
        totalEmployees?: number;
        totalEntitlements?: number;
        totalUnpaidWages?: number;
        totalUnpaidSuper?: number;
        mergedCount?: number;
      }
    | undefined;

  return (
    <Tabs
      defaultValue={isRunning ? "feed" : "employees"}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tabs */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">Preferred Creditors</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {(isCompleted || (entries && entries.length > 0)) && (
              <ExportButton
                content={buildPreferredCreditorsExport(entries ?? [])}
                filename="Preferred_Creditors_Summary"
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            <TabsTrigger value="employees" className="gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Preferred Creditors
              {output?.totalEmployees != null && output.totalEmployees > 0 && (
                <Badge variant="secondary" className="ml-0.5 px-1 text-[10px]">
                  {output.totalEmployees}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting state */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="rounded-full bg-muted/50 p-4">
            <Users className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Ready to analyse</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Identifies employee priority creditors and individual entitlements
              from payroll, ROCAPs, and aged payables.
            </p>
          </div>
          {!isViewer && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setManualEntry(true)}
            >
              <Plus className="h-3.5 w-3.5" /> Enter manually
            </Button>
          )}
        </div>
      )}

      {/* Active / completed — tab content */}
      {!isWaiting && (
        <>
          <TabsContent value="employees" className="mt-2">
            <EmployeeEntitlementsPanel
              jobId={jobId}
              taskId={taskId}
              isRunning={isRunning}
              isViewer={isViewer}
            />
          </TabsContent>

          <TabsContent value="feed" className="mt-2">
            <UnifiedFeed
              task={task}
              subAgents={[]}
              taskTree={task.workingMemory?.taskTree}
              isRunning={isRunning}
            />
          </TabsContent>
        </>
      )}

      {isCompleted && task.output && (
        <SummaryChatSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          taskId={taskId}
          jobId={jobId}
          agentName="Preferred Creditors"
        />
      )}
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
